import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { documentAPI, panelAPI } from "../services/api";

export const usePanelData = (panelId) => {
  const [panel, setPanel] = useState(null);
  const [panelists, setPanelists] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("IN_REVIEW");
  const [finalReport, setFinalReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPanelData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch panel data from backend
      const panelResponse = await panelAPI.getPanelById(panelId);

      if (panelResponse.success && panelResponse.panel) {
        const currentPanel = panelResponse.panel;
        setPanel(currentPanel);
        setStatus(currentPanel.status || "IN_REVIEW");
        setProgress(currentPanel.progress || 0);

        // Set panel members as panelists
        if (currentPanel.members && currentPanel.members.length > 0) {
          const formattedPanelists = currentPanel.members.map((member) => ({
            id: member.id,
            name: member.user?.name || "Unknown",
            email: member.user?.email || "",
            expertise: member.expertise || [],
            panelId: parseInt(panelId),
          }));
          setPanelists(formattedPanelists);
        }
      }

      // Fetch documents for the panel
      const docsResponse = await documentAPI.getPanelDocuments(panelId);
      if (docsResponse.success) {
        setDocuments(docsResponse.documents || []);
      }

      // Check localStorage for final report (backward compatibility)
      const savedReport = localStorage.getItem(`finalReport_${panelId}`);
      if (savedReport) {
        setFinalReport(JSON.parse(savedReport));
      }
    } catch (error) {
      console.error("Error fetching panel data:", error);
      toast.error("Failed to load panel data");
      throw error;
    } finally {
      setLoading(false);
    }
  }, [panelId]);

  useEffect(() => {
    if (panelId) {
      fetchPanelData();
    }
  }, [panelId, fetchPanelData]);

  const updateProgress = async (increment) => {
    const newProgress = Math.min(100, progress + increment);
    setProgress(newProgress);

    try {
      await panelAPI.updatePanel(panelId, { progress: newProgress });
      toast.success("Progress updated successfully");
    } catch (error) {
      console.error("Error updating progress:", error);
      toast.error("Failed to update progress");
      setProgress(progress); // Revert on error
    }
  };

  const updateStatus = (newStatus) => {
    setStatus(newStatus);
  };

  const updateDocuments = (newDocuments) => {
    setDocuments(newDocuments);
  };

  const updateFinalReport = (report) => {
    setFinalReport(report);
  };

  return {
    panel,
    panelists,
    documents,
    progress,
    status,
    finalReport,
    loading,
    fetchPanelData,
    updateProgress,
    updateStatus,
    updateDocuments,
    updateFinalReport,
    setProgress,
  };
};
